import { CardBasic } from "@/components/ui-library-new/card/CardBasic"
import { BlokProps } from "@/helpers/StoryblokHelpers"
import { VaccineWithCampaign } from "@/storyblok/vaccinationRelations"
import { VaccineTypeInfoCardSectionStoryblok } from "@/types/component-types"
import { cn } from "@/utils/utils"
import { campaignDiscount, isCampaignActive } from "@/utils/vaccineCampaign"
import { numericPrice } from "@/utils/vaccineLabels"
import { SbBlokData, storyblokEditable, StoryblokServerComponent } from "@storyblok/react/rsc"

type VaccineTypeInfoCardSectionProps = BlokProps<VaccineTypeInfoCardSectionStoryblok> & {
  vaccine: VaccineWithCampaign
  /**
   * The vaccines this one names in `relation_id`, already resolved by the page.
   *
   * Listed under it in the price table, in the order the editor set. The relation is symmetric and
   * hand-maintained: Grundskydd names Grundskydd +, and Grundskydd + names Grundskydd, so whichever
   * page you are on shows itself first and the other beneath.
   */
  relatedVaccines?: VaccineWithCampaign[]
}

const labelBase = "text-[15px] font-semibold leading-[22px] tracking-[-0.045em]"
const labelClasses = `${labelBase} lg:text-[20px] lg:leading-[27px]`

/** "Gratis" rather than "0 / dos" — a free vaccine is a different fact, not a price of zero. */
const priceLabel = (value: string | undefined) => (Number(value) === 0 ? "Gratis" : `${value} kr / dos`)

/**
 * `campaign_price` holds the DISCOUNT, not the reduced price: Bältros stores 300 against a price of
 * 2650 and sells for 2350. Same arithmetic as the price cards, so the two cannot disagree.
 */
const discountedPrice = (value: string | undefined, discount: string) => String(Number(value) - Number(discount))

/**
 * A price, struck through and followed by the campaign price when one runs. The unit is written
 * once, on the price that is actually charged — repeating "/ dos" on both makes the cell noise.
 */
const Price = ({ value, discount }: { value: string | undefined; discount?: string }) => {
  // `numericPrice` is null for a range like pneumokocker's "490-1150" — a discount cannot be
  // struck from it, so it renders exactly as the editor wrote it.
  const price = numericPrice(value)
  if (!discount || price === null || price === 0) return <>{priceLabel(value)}</>

  return (
    <>
      <span className="text-neutral-30 line-through">{value}</span> {priceLabel(discountedPrice(value, discount))}
    </>
  )
}

export const VaccineTypeInfoCardSection = ({ blok, vaccine, relatedVaccines }: VaccineTypeInfoCardSectionProps) => {
  const { content } = blok

  // This vaccine first, then the ones it relates to. One list so the mobile block and the table
  // cannot drift apart — they used to render the same single vaccine from separate variables.
  const rows = [vaccine, ...(relatedVaccines ?? [])].filter(Boolean)

  // The Barn column belongs to the TABLE, not to a row: one adults-only vaccine among several must
  // not remove a column the others need. Rows that are adults-only show a dash in it instead.
  const showChildColumn = rows.some(row => !row.adult_only)

  return (
    <section
      {...storyblokEditable(blok as SbBlokData)}
      className="mx-auto mb-4 mt-5 flex w-full max-w-[1155px] flex-col gap-8 px-4 lg:mt-[17px] lg:gap-10 lg:px-8"
    >
      <CardBasic
        variant="softElevated"
        className="min-h-0 gap-2 p-4 leading-[14px] lg:p-[34px] [&_h2]:mb-4 [&_h2]:text-[16px] [&_h2]:font-semibold [&_h2]:leading-[22px] [&_h2]:tracking-[-0.045em] lg:[&_h2]:text-[20px] lg:[&_h2]:font-semibold lg:[&_h2]:leading-[27px] [&_p]:text-sm [&_p]:leading-5 [&_p]:tracking-normal lg:[&_p]:font-normal"
      >
        {isCampaignActive(vaccine)
          ? vaccine.campaign_description?.map(nested => <StoryblokServerComponent key={nested._uid} blok={nested} />)
          : content?.map(nested => <StoryblokServerComponent key={nested._uid} blok={nested} />)}
      </CardBasic>
      <CardBasic
        variant="default"
        className="min-h-0 flex-none gap-2 p-4 leading-[14px] lg:p-[34px] [&_h2]:mb-4 [&_h2]:text-[16px] [&_h2]:font-semibold [&_h2]:leading-[22px] [&_h2]:tracking-[-0.045em] lg:[&_h2]:text-[20px] lg:[&_h2]:font-semibold lg:[&_h2]:leading-[27px] [&_p]:leading-5 [&_p]:tracking-normal lg:[&_p]:font-normal"
      >
        {/* Mobile */}
        <div className="lg:hidden">
          <p className={cn(labelBase, "text-neutral-90")}>Pris vaccination</p>

          {rows.map((row, index) => (
            <div key={row._uid ?? index} className={index > 0 ? "mt-8 border-t border-[#E7E7E7] pt-6" : undefined}>
              <p className={cn(labelBase, "mt-3 text-neutral-50")}>{row.name}</p>
              {row.show_required_amount === true && row.required_amount && (
                <p className="text-sm font-normal leading-5 text-neutral-50">{row.required_amount}</p>
              )}

              <div className={cn("mt-8 grid grid-cols-2 gap-4", labelBase, "text-neutral-50")}>
                <span>Vuxen</span>
                {showChildColumn && <span>Barn</span>}
              </div>
              <div className="mt-3 border-b border-[#E7E7E7]" />
              <div className={cn("mt-4 grid grid-cols-2 gap-4", labelBase, "text-neutral-50")}>
                <span className="whitespace-nowrap">
                  <Price value={row.price_adult} discount={campaignDiscount(row)} />
                </span>
                {showChildColumn && (
                  <span className="whitespace-nowrap">
                    {row.adult_only ? "—" : <Price value={row.price_child} discount={campaignDiscount(row)} />}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
        {/* Not mobile */}
        <table className="hidden w-full lg:table">
          <thead>
            <tr
              className={cn(
                "[&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-[#E7E7E7] [&_th]:pb-6",
                labelClasses
              )}
            >
              <th className="text-left text-neutral-90">Pris vaccination</th>
              <th className="text-left text-neutral-50">Vuxen</th>
              {showChildColumn && <th className="text-left text-neutral-50">Barn</th>}
            </tr>
          </thead>

          <tbody>
            {rows.map((row, index) => (
              <tr
                key={row._uid ?? index}
                className={cn(
                  "[&_td]:whitespace-nowrap [&_td]:pt-9 [&_td]:align-top [&_td]:text-neutral-50",
                  labelClasses
                )}
              >
                <td>
                  {row.name}
                  {row.show_required_amount === true && row.required_amount && (
                    <span className="block text-sm font-normal leading-5 tracking-normal">{row.required_amount}</span>
                  )}
                </td>
                <td className="text-left">
                  <Price value={row.price_adult} discount={campaignDiscount(row)} />
                </td>
                {/* A dash, not an empty cell: "not applicable" is the fact, and a blank reads as a
                    price nobody filled in. */}
                {showChildColumn && (
                  <td className="text-left">
                    {row.adult_only ? "—" : <Price value={row.price_child} discount={campaignDiscount(row)} />}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </CardBasic>
    </section>
  )
}
